import React, { useState, useEffect } from 'react';
import { User, LogOut } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const Settings = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    role: '',
    created_at: '',
    drive_folder_link: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Load real user data from Supabase auth
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError) {
        console.error('Failed to get user:', authError);
        toast.error('Failed to load user profile');
        return;
      }

      if (user) {
        // Fetch user data from users table
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('Error fetching user data:', userError);
          toast.error('Failed to load user data');
          return;
        }

        if (userData) {
          setProfile({
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role || 'team_member',
            created_at: userData.created_at || '',
            drive_folder_link: userData.drive_folder_link || ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      toast.error('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile.name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    try {
      setSaving(true);
      
      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Not authenticated');
        return;
      }

      // Update name in users table
      const { error } = await supabase
        .from('users')
        .update({ name: profile.name.trim() })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Profile updated successfully!');
      
      // Reload profile to ensure we have fresh data
      await loadUserProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        const { error } = await supabase.auth.signOut();

        if (error) {
          console.error('Logout error:', error);
          toast.error('Failed to logout: ' + error.message);
          return;
        }

        // Clear any stored data (if any)

        toast.success('Logged out successfully');
        navigate('/login');
      } catch (error) {
        console.error('Logout error:', error);
        toast.error('Failed to logout');
      }
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="settings-page">
      <h1 className="text-3xl font-bold mb-8">Settings</h1>

      <div className="space-y-6">
        {/* Profile Settings */}
        <div className="glass-card p-6" data-testid="profile-section">
          <h2 className="text-xl font-semibold mb-4">Profile Settings</h2>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="loading-dots mb-4">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <p className="text-gray-400">Loading profile...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-6 mb-6">
                <div className="relative group">
                  <div className="w-24 h-24 bg-black border-2 border-white/20 ring-4 ring-white/5 rounded-full flex items-center justify-center text-white font-bold text-4xl shadow-[0_0_25px_rgba(255,255,255,0.05)]">
                    {profile.name ? profile.name.charAt(0).toUpperCase() : <User size={40} />}
                  </div>
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer border border-white/20">
                    <span className="text-xs font-semibold text-white">Change</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{profile.name}</h3>
                  <p className="text-sm text-gray-400">{profile.role}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="w-full glass-input"
                    placeholder="Enter your name"
                    disabled={saving}
                    data-testid="profile-name-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">You can update your name</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={profile.email}
                    readOnly
                    className="w-full glass-input bg-white/5 cursor-not-allowed"
                    data-testid="profile-email-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Role</label>
                  <input
                    type="text"
                    value={profile.role === 'admin' ? 'Admin' : 'Team Member'}
                    readOnly
                    className="w-full glass-input bg-white/5 cursor-not-allowed"
                    data-testid="profile-role-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">Role cannot be changed</p>
                </div>

                {profile.drive_folder_link && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Google Drive Folder</label>
                    <a
                      href={profile.drive_folder_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 text-sm underline break-all"
                    >
                      {profile.drive_folder_link}
                    </a>
                  </div>
                )}

                {profile.created_at && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Member Since</label>
                    <input
                      type="text"
                      value={new Date(profile.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      readOnly
                      className="w-full glass-input bg-white/5 cursor-not-allowed"
                    />
                  </div>
                )}

                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-profile-btn"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Preferences */}
        <div className="glass-card p-6" data-testid="preferences-section">
          <h2 className="text-xl font-semibold mb-4">Preferences</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Theme</label>
              <select className="w-full glass-input cursor-pointer" disabled data-testid="theme-select">
                <option value="dark">Dark (Default)</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">Light theme coming soon</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notifications</label>
              <div className="glass-card p-4 text-center text-gray-400 text-sm">
                Notification settings coming soon
              </div>
            </div>
          </div>
        </div>

        {/* About */}
        <div className="glass-card p-6" data-testid="about-section">
          <h2 className="text-xl font-semibold mb-4">About</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Version</span>
              <span className="font-medium">1.0.0</span>
            </div>
            <div>
              <p className="text-sm text-gray-400">
                Cortex is an R&D Knowledge Management System designed to track problems, provide AI-powered insights, and manage technical documentation.
              </p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="mt-6 w-full btn-secondary text-red-400 hover:bg-red-500/10 flex items-center justify-center gap-2"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
